import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { IconButton } from '#src/atoms/buttons/IconButton/index.tsx';
import { iconPlus } from '#src/atoms/icons/system/iconPlus.ts';
import { iconTrash } from '#src/atoms/icons/system/iconTrash.ts';
import { TextInput } from '#src/atoms/inputs/TextInput/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyS, HeadlineS } from '#src/atoms/typography/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { GraphqlForm } from '#src/components/form/Form/index.tsx';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import type { useMutation } from '#src/hooks/graphql/useMutation.ts';

import type {
  UpdateOAuthApplicationMutation,
  UpdateOAuthApplicationMutationVariables,
} from '../__generated__/index.graphql.ts';

type UpdateApplicationFn = ReturnType<
  typeof useMutation<
    UpdateOAuthApplicationMutation,
    UpdateOAuthApplicationMutationVariables
  >
>[0];

const messages = defineMessages({
  title: {
    defaultMessage: 'Edit callback URLs',
  },
  callbackUrls: {
    defaultMessage: 'Callback URLs',
  },
  addCallbackUrl: {
    defaultMessage: 'Add a callback URL',
  },
  removeCallbackUrl: {
    defaultMessage: 'Remove this callback URL',
  },
  save: {
    defaultMessage: 'Save',
  },
});

const StyledGraphqlForm = styled(GraphqlForm)`
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
  padding: var(--double-unit);
`;

type Props = {
  open: boolean;
  onClose: () => void;
  applicationId: string;
  currentRedirectUris: string[];
  updateApplication: UpdateApplicationFn;
};

type FormProps = Omit<Props, 'open'>;

const EditCallbackUrlsForm = ({
  onClose,
  applicationId,
  currentRedirectUris,
  updateApplication,
}: FormProps) => {
  const { formatMessage } = useIntlContext();
  const [redirectUris, setRedirectUris] = useState<string[]>(
    currentRedirectUris.length > 0 ? currentRedirectUris : ['']
  );

  const cleanedUris = redirectUris.map(uri => uri.trim()).filter(Boolean);
  const canSubmit = cleanedUris.length > 0;

  return (
    <StyledGraphqlForm
      onSubmit={(_values, onResult) => {
        updateApplication({
          variables: {
            input: {
              id: applicationId,
              redirectUris: cleanedUris,
            },
          },
        }).then(onResult);
      }}
      onSuccess={onClose}
      render={(Error, SubmitButton) => (
        <>
          <Vertical gap={1}>
            <BodyS bold>
              <FormattedMessage {...messages.callbackUrls} />
            </BodyS>
            {redirectUris.map((uri, index) => (
              <Horizontal
                gap={1}
                // oxlint-disable-next-line react/no-array-index-key
                key={index}
              >
                <TextInput
                  type="text"
                  placeholder="https://example.com/callback"
                  value={uri}
                  onChange={e =>
                    setRedirectUris(uris =>
                      uris.map((u, i) => (i === index ? e.target.value : u))
                    )
                  }
                />
                {redirectUris.length > 1 && (
                  <IconButton
                    type="button"
                    size="medium"
                    color="quaternary"
                    icon={iconTrash}
                    aria-label={formatMessage(messages.removeCallbackUrl)}
                    onClick={() =>
                      setRedirectUris(uris =>
                        uris.filter((_, i) => i !== index)
                      )
                    }
                  />
                )}
              </Horizontal>
            ))}
            <div>
              <IconButton
                type="button"
                size="medium"
                color="tertiary"
                icon={iconPlus}
                aria-label={formatMessage(messages.addCallbackUrl)}
                onClick={() => setRedirectUris(uris => [...uris, ''])}
              />
            </div>
          </Vertical>
          <Error code />
          <SubmitButton size="large" disabled={!canSubmit}>
            <FormattedMessage {...messages.save} />
          </SubmitButton>
        </>
      )}
    />
  );
};

export const EditCallbackUrlsDialog = ({ open, onClose, ...rest }: Props) => (
  <Dialog
    fullWidth
    maxWidth="xs"
    open={open}
    onClose={onClose}
    title={
      <HeadlineS as="h5" className="text-center">
        <FormattedMessage {...messages.title} />
      </HeadlineS>
    }
  >
    <EditCallbackUrlsForm onClose={onClose} {...rest} />
  </Dialog>
);
